import React, { useState, useEffect } from 'react'
import get from 'lodash/get'
import debounce from 'lodash/debounce'
import ContentClear from '@material-ui/icons/Clear'
import ListMUITextField from './ListMUITextField'
import DatePickerValidate from './DatePickerValidate'
import RenderDatepickerFilter from './RenderDatePickerFilter'
import ListMUISelectField, { defaultSelectMenuProps } from './ListMUISelectField'
import RenderRangePicker from '../Form/RenderRangePicker'
import { datePickerFormat } from '../../services/ValidationRules'
import Checkbox from '@material-ui/core/Checkbox'
import ListItemText from '@material-ui/core/ListItemText'
import MenuItem from '@material-ui/core/MenuItem'
import FormControl from '@material-ui/core/FormControl'
import { SelectWrap } from '../Select/style'
import styled from 'styled-components'

const ALL_OPTION = 'All'
const ALL_OPTION_VALUE = '__all__'

const StyledCheckBoxMenuItem = styled(MenuItem)`
  && {
    padding: 6px 10px;
    gap: 5px;
  }
`

export const onSeparatedTextFilter = ({ filter, onChange }) => {
  const forwardChange = debounce(onChange, 500)
  const filterVal = get(filter, 'value')

  return (
    <ListMUITextField
      defaultValue={filterVal}
      onChange={
        ({ target: { value } }) =>
          value
            ? forwardChange(value.split(',').map(item => item.trim()))
            : forwardChange('')
      }
    />
  )
}

export const onTextFilter = ({ filter, onChange }) => {
  const forwardChange = debounce(onChange, 500)
  const filterVal = get(filter, 'value')
  return (
    <ListMUITextField
      defaultValue={filterVal}
      onChange={(e) => forwardChange(e.target.value)}
    />
  )
}

export const onDataFilterSSP = ({ filter, onChange }) => {
  const filterVal = get(filter, 'value')
  return (
    <DatePickerValidate
      defaultValue={filterVal}
      filter={filter}
      onChange={onChange}
    />
  )
}

export const onDatePickerFilterTimeStampOutput = ({ filter, onChange }) => (
  <RenderDatepickerFilter
    onChange={onChange}
    timestampOutput
    value={filter ? filter.value : ''}
  />
)

export const onFilterDateRangePicker = ({ onChange }) => (
  <RenderRangePicker
    input={{
      onChange: (val) => onChange({
        start_timestamp: val.startDate ? val.startDate.utc().startOf('day').valueOf() : '',
        end_timestamp: val.endDate ? val.endDate.utc().endOf('day').valueOf() : ''
      })
    }}
    className='filter-picker'
    pickerProps={{
      displayFormat: datePickerFormat,
      anchorDirection: 'right',
      appendToBody: true,
      readOnly: true,
      block: true,
      customCloseIcon: <ContentClear />,
      small: true,
      minimumNights: 0
    }}
  />
)

export const SelectFilter = ({ onChange, value, options }) => (
  <ListMUISelectField
    onChange={value => {
      onChange(value)
    }}
    value={value}
    children={options}
  />
)

export const MultiSelectFilter = ({ onChange, value, options }) => {
  const optionNames = options.map(option => option.name)

  const optionLabels = options.reduce((acc, option) => {
    acc[option.name] = option.label

    return acc
  }, {})

  // Visual state is tracked locally because both "all" and "nothing" send [] to BE
  // Initialize as "all selected" when value is an empty array (which is the BE representation of "all")
  const [allVisuallySelected, setAllVisuallySelected] = useState(Array.isArray(value) && value.length === 0)

  // Sync when filter is reset externally (value becomes null/undefined)
  useEffect(() => {
    // A non-array value means the filter was cleared — deselect "All"
    if (!Array.isArray(value)) {
      setAllVisuallySelected(false)
    }
  }, [value])

  // When "All" is visually selected, show every option as checked
  // Otherwise use the actual selected values, falling back to empty if value is not an array
  const selectedOptionNames = allVisuallySelected ? optionNames : Array.isArray(value) ? value : []

  const applySelection = (visuallyAll, filterValue) => {
    setAllVisuallySelected(visuallyAll)

    onChange(filterValue)
  }

  const handleChange = ({ target: { value: selectedValues } }) => {
    // User clicked the "All" option — toggle its visual state and send [] to BE
    if (selectedValues.includes(ALL_OPTION_VALUE)) return applySelection(prev => !prev, [])

    // User deselected the last remaining option — clear everything
    if (selectedValues.length === 0) return applySelection(false, [])

    // All individual options are now checked — treat it as "All" and send [] to BE
    if (selectedValues.length === optionNames.length) return applySelection(true, [])

    // A partial subset of options is selected — pass them as-is to BE
    applySelection(false, selectedValues)
  }

  const renderSelectedValues = (selectedValues) => {
    // "All" is visually active — show the "All" label instead of listing every option
    if (allVisuallySelected) return ALL_OPTION

    // Nothing is selected — show an empty placeholder
    if (selectedValues.length === 0) return ''

    return selectedValues
      .map(optionName => optionLabels[optionName] || optionName)
      .join(', ')
  }

  return (
      <FormControl variant="outlined" size="small" fullWidth>
        <SelectWrap
            multiple
            displayEmpty
            value={selectedOptionNames}
            onChange={handleChange}
            renderValue={renderSelectedValues}
            MenuProps={defaultSelectMenuProps}
        >
          <StyledCheckBoxMenuItem value={ALL_OPTION_VALUE}>
            <Checkbox
                checked={allVisuallySelected}
                // Show dash when some (but not all) options are selected
                indeterminate={selectedOptionNames.length > 0 && !allVisuallySelected}
            />
            <ListItemText primary={ALL_OPTION} />
          </StyledCheckBoxMenuItem>

          {options.map(option => (
              <StyledCheckBoxMenuItem
                  key={option.name}
                  value={option.name}
              >
                <Checkbox
                    checked={selectedOptionNames.includes(option.name)}
                />
                <ListItemText primary={option.label} />
              </StyledCheckBoxMenuItem>
          ))}
        </SelectWrap>
      </FormControl>
  )
}
